import Fuse, { type FuseOptionKey, type IFuseOptions } from 'fuse.js';

import type { ExplorationVolume } from '../types/exploration';

function tagsText(volume: ExplorationVolume): string {
  if (!volume.tags) return '';
  return Object.entries(volume.tags)
    .map(([key, value]) => `${key} ${value}`)
    .join(' ');
}

// Identity fields (ebsId/name) outweigh descriptive fields, which outweigh short status
// labels (awsState/awsStatus/activationLabel/supportLabel) — those are low-cardinality
// strings a short query can match by accident, so downweighting keeps incidental hits
// out of top results (and below the threshold entirely, if nothing else matches).
const fuseKeys: FuseOptionKey<ExplorationVolume>[] = [
  { name: 'ebsId', weight: 2 },
  { name: 'name', weight: 2 },
  { name: 'cluster', weight: 1 },
  { name: 'nodeGroup', weight: 1 },
  { name: 'instanceId', weight: 1 },
  { name: 'region', weight: 1 },
  { name: 'diskType', weight: 1 },
  { name: 'accountName', weight: 1 },
  { name: 'availabilityZone', weight: 1 },
  { name: 'awsState', weight: 0.5 },
  { name: 'awsStatus', weight: 0.5 },
  { name: 'activationLabel', weight: 0.5 },
  { name: 'supportLabel', weight: 0.5 },
  { name: 'tagsText', weight: 1, getFn: tagsText },
];

const fuseOptions: IFuseOptions<ExplorationVolume> = {
  keys: fuseKeys,
  threshold: 0.3,
};

// Building the Fuse index is the expensive part (it precomputes per-key data for every
// volume), and the caller re-derives its search results on every keystroke while `volumes`
// itself is usually unchanged — so cache the index against the last `volumes` reference
// rather than rebuilding it on each call.
let cachedVolumes: ExplorationVolume[] | null = null;
let cachedFuse: Fuse<ExplorationVolume> | null = null;

function getFuse(volumes: ExplorationVolume[]): Fuse<ExplorationVolume> {
  if (cachedVolumes !== volumes || !cachedFuse) {
    cachedFuse = new Fuse(volumes, fuseOptions);
    cachedVolumes = volumes;
  }
  return cachedFuse;
}

export function searchVolumes(
  volumes: ExplorationVolume[],
  query: string
): ExplorationVolume[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return volumes;

  return getFuse(volumes)
    .search(trimmedQuery)
    .map((result) => result.item);
}
